import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  listTeamMembers,
  inviteTeamMember,
  removeTeamMember,
  type TeamMember,
} from "../../services/teamService";
import { usePermissionLevel } from "../../hooks/usePermissionLevel";
import InviteModal from "../admin/InviteModal";
import ConfirmModal from "../admin/ConfirmModal";
import Toast, { type ToastItem } from "../admin/Toast";

export interface TeamPageProps {
  getToken: () => string | undefined;
}

/**
 * TeamPage — manager-facing member management, scoped to the caller's own
 * associated members. Reduced form of AdminPage's MembersTab: no cross-org
 * search or pagination (GET /team/members returns the manager's whole,
 * single-org set in one call), no role column or role-change control (a
 * manager only ever creates plain members and cannot promote anyone), and no
 * System Prompts tab.
 *
 * A member's own row never appears here, and neither does an Organization
 * column — every row is already scoped to the caller's own org and the
 * server does not return an org-name map for this endpoint (unlike the admin
 * listing, which spans every org and needs one).
 */
const TeamPage: React.FC<TeamPageProps> = ({ getToken }) => {
  const { isManagerOrAbove } = usePermissionLevel();

  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<TeamMember | null>(null);
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  // Same discard-stale-response pattern as MembersTab, in case unmount +
  // refetch ever race.
  const requestIdRef = useRef(0);

  const pushToast = useCallback((kind: "success" | "error", message: string) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setToasts((prev) => [...prev.slice(-2), { id, kind, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3000);
  }, []);

  const fetchMembers = useCallback(
    async (signal?: AbortSignal) => {
      const myRequestId = ++requestIdRef.current;
      try {
        setLoading(true);
        setError(null);
        const response = await listTeamMembers(getToken(), signal);
        if (requestIdRef.current !== myRequestId) return;
        setMembers(response.members);
      } catch (e: any) {
        if (e?.name === "AbortError") return;
        if (requestIdRef.current !== myRequestId) return;
        setError(e?.message ?? "Failed to load team members");
      } finally {
        if (requestIdRef.current === myRequestId) {
          setLoading(false);
        }
      }
    },
    [getToken],
  );

  useEffect(() => {
    if (!isManagerOrAbove) return;
    const controller = new AbortController();
    fetchMembers(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManagerOrAbove]);

  const displayName = (m: TeamMember) => m.name || m.email_address;

  if (!isManagerOrAbove) {
    return (
      <div className="flex flex-col flex-1 h-full min-w-0 overflow-y-auto bg-background">
        <div className="max-w-5xl w-full mx-auto px-8 py-10 flex flex-col items-center justify-center text-center min-h-full">
          <span className="material-symbols-outlined text-5xl text-slate-500 mb-4">
            lock
          </span>
          <h2 className="font-headline text-3xl font-bold tracking-tight text-on-surface mb-2">
            Manager access required
          </h2>
          <p className="text-slate-500 font-body text-sm max-w-md">
            You need to be a manager to access this page. Contact an
            administrator in your organization if you believe this is a
            mistake.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 h-full min-w-0 overflow-y-auto bg-background">
      <div className="max-w-5xl w-full mx-auto px-8 py-10">
        <div className="mb-8">
          <h2 className="font-headline text-3xl font-bold tracking-tight text-on-surface mb-1">
            Team Members
          </h2>
          <p className="text-slate-500 font-label text-xs uppercase tracking-widest">
            Members you manage
          </p>
        </div>

        <div className="flex items-center justify-end gap-4 mb-6">
          <button
            onClick={() => setInviteOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-on-primary font-bold text-xs rounded-md uppercase tracking-tighter font-label transition-all active:opacity-80 shadow-[0_0_15px_-3px_rgba(60,221,199,0.4)]"
          >
            <span className="material-symbols-outlined text-base">person_add</span>
            Invite Member
          </button>
        </div>

        {loading && (
          <div className="bg-surface-container-low rounded-lg border border-outline-variant/20 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-surface-container border-b border-outline-variant/20">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-label font-bold uppercase tracking-wider text-slate-400">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-label font-bold uppercase tracking-wider text-slate-400">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-label font-bold uppercase tracking-wider text-slate-400">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-label font-bold uppercase tracking-wider text-slate-400 w-10" />
                </tr>
              </thead>
              <tbody>
                {[0, 1, 2].map((i) => (
                  <tr key={i} className="border-b border-outline-variant/10 animate-pulse">
                    <td className="px-4 py-3"><div className="h-4 bg-surface-container-high rounded w-48" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-surface-container-high rounded w-32" /></td>
                    <td className="px-4 py-3"><div className="h-5 bg-surface-container-high rounded-full w-14" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-surface-container-high rounded w-5" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-24 gap-3 text-error">
            <span className="material-symbols-outlined text-4xl">cloud_off</span>
            <p className="font-label text-sm uppercase tracking-widest">Failed to load team members</p>
            <p className="text-xs font-body text-slate-500 text-center max-w-xs">
              Check your connection and try again.
            </p>
            <button
              onClick={() => fetchMembers()}
              className="px-4 py-2 bg-surface-container hover:bg-surface-container-high border border-outline-variant/30 rounded text-on-surface text-xs font-label uppercase tracking-wider transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && members.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 gap-3 text-slate-500">
            <span className="material-symbols-outlined text-4xl opacity-20">group</span>
            <p className="font-label text-sm uppercase tracking-widest opacity-60">No members yet</p>
            <p className="text-xs font-body text-slate-500 text-center max-w-xs">
              Invite your first team member to get started.
            </p>
          </div>
        )}

        {!loading && !error && members.length > 0 && (
          <div className="bg-surface-container-low rounded-lg border border-outline-variant/20 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-surface-container border-b border-outline-variant/20">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-label font-bold uppercase tracking-wider text-slate-400">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-label font-bold uppercase tracking-wider text-slate-400">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-label font-bold uppercase tracking-wider text-slate-400">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-label font-bold uppercase tracking-wider text-slate-400 w-10" />
                </tr>
              </thead>
              <tbody>
                {members.map((member) => {
                  const isPending = member.status !== "active";
                  const statusPillClasses = !isPending
                    ? "text-xs font-label font-bold tracking-widest uppercase px-2 py-1 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/30"
                    : "text-xs font-label font-bold tracking-widest uppercase px-2 py-1 rounded bg-outline-variant/30 text-on-surface-variant border border-outline-variant/50";
                  return (
                    <tr key={member.member_id} className="border-b border-outline-variant/10 hover:bg-surface-container-high transition-colors">
                      <td className="px-4 py-3 text-sm font-body text-on-surface">
                        <span className="truncate block max-w-xs">{member.email_address}</span>
                      </td>
                      <td className="px-4 py-3 text-sm font-body text-on-surface">
                        {isPending ? (
                          <span className="text-on-surface-variant">—</span>
                        ) : (
                          member.name || <span className="text-on-surface-variant">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm font-body text-on-surface">
                        <span className={statusPillClasses}>{isPending ? "Pending" : "Active"}</span>
                      </td>
                      <td className="px-4 py-3 text-sm font-body text-on-surface">
                        <button
                          onClick={() => setRemoveTarget(member)}
                          className="p-2 rounded text-slate-500 hover:text-error hover:bg-surface-container-highest transition-colors"
                          aria-label={`Remove ${displayName(member)} from your team`}
                          title="Remove from team"
                        >
                          <span className="material-symbols-outlined text-base">person_remove</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && !error && members.length > 0 && (
          <p className="text-xs font-label text-slate-500 text-center mt-4">
            Showing {members.length} member{members.length === 1 ? "" : "s"}
          </p>
        )}

        {inviteOpen && (
          <InviteModal
            getToken={getToken}
            hideRoleSelector
            onInvite={async (email, _role, token) => {
              await inviteTeamMember(email, token);
            }}
            onClose={() => setInviteOpen(false)}
            onSuccess={(email) => {
              pushToast("success", `Invitation sent to ${email}`);
              fetchMembers();
            }}
            onError={(message) => pushToast("error", message)}
          />
        )}

        {removeTarget && (
          <ConfirmModal
            title="Remove From Team?"
            body={`${displayName(removeTarget)} will be removed from your team. They keep their account and any access other managers have granted them — you can re-add them at any time.`}
            icon="person_remove"
            confirmLabel="Remove"
            cancelLabel="Keep on Team"
            destructive
            onClose={() => setRemoveTarget(null)}
            onConfirm={async () => {
              try {
                await removeTeamMember(removeTarget.member_id, getToken());
                setRemoveTarget(null);
                pushToast("success", "Member removed from your team");
                fetchMembers();
              } catch {
                setRemoveTarget(null);
                pushToast("error", "Failed to remove member. Try again.");
              }
            }}
          />
        )}

        <Toast toasts={toasts} />
      </div>
    </div>
  );
};

export default TeamPage;
